import { Link } from 'react-router-dom';
import { ArrowLeft, Shield } from 'lucide-react';
import PublicNav from '../components/PublicNav';
import PublicFooter from '../components/PublicFooter';
import BackToTop from '../components/ui/BackToTop';

const EFFECTIVE_DATE = 'June 15, 2026';
const ORG_NAME = 'Second Story Initiative';
const CONTACT_EMAIL = 'eHunter@straydog-secondstory.org';
const DOMAIN = 'straydog-secondstory.org';

const PrivacyPolicy = () => {
  return (
    <div className="min-h-screen bg-white">
      <PublicNav />

      <main id="main-content" className="py-16 sm:py-20">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Header */}
          <div className="mb-10">
            <Link
              to="/"
              className="inline-flex items-center text-sm text-primary-600 hover:text-primary-500 no-underline hover:no-underline mb-6"
            >
              <ArrowLeft className="h-4 w-4 mr-1" />
              Back to Home
            </Link>
            <div className="flex items-center gap-3 mb-4">
              <div className="h-10 w-10 rounded-lg bg-primary-100 flex items-center justify-center">
                <Shield className="h-5 w-5 text-primary-600" aria-hidden="true" />
              </div>
              <h1 className="text-3xl font-bold text-secondary-900">Privacy Policy</h1>
            </div>
            <p className="text-sm text-secondary-500">
              Effective: {EFFECTIVE_DATE} · Last updated: {EFFECTIVE_DATE}
            </p>
          </div>

          {/* Content */}
          <div className="prose prose-secondary max-w-none space-y-8">
            <section>
              <h2 className="text-xl font-semibold text-secondary-900 mb-3">1. Who We Are</h2>
              <p className="text-secondary-700 leading-relaxed">
                {ORG_NAME} is a Rhode Island nonprofit corporation that empowers justice-impacted
                individuals to build meaningful careers in technology. This Privacy Policy explains
                how we collect, use, and protect your information when you visit{' '}
                <strong>{DOMAIN}</strong> or use our platform services.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-secondary-900 mb-3">2. Information We Collect</h2>
              <h3 className="text-lg font-medium text-secondary-800 mt-4 mb-2">Information You Provide</h3>
              <ul className="list-disc pl-5 space-y-1 text-secondary-700">
                <li>Account registration data (name, email address, password)</li>
                <li>Profile information (skills, bio, learning goals)</li>
                <li>Mentorship communications and session notes</li>
                <li>Project submissions and code review content</li>
                <li>Donation information (processed by our third-party payment provider)</li>
                <li>Communications with us (email, contact forms)</li>
              </ul>

              <h3 className="text-lg font-medium text-secondary-800 mt-4 mb-2">Information Collected Automatically</h3>
              <ul className="list-disc pl-5 space-y-1 text-secondary-700">
                <li>Device and browser type, operating system</li>
                <li>IP address and approximate location (country/region level)</li>
                <li>Pages visited, features used, and time spent on the platform</li>
                <li>Referral source</li>
              </ul>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-secondary-900 mb-3">3. How We Use Your Information</h2>
              <ul className="list-disc pl-5 space-y-1 text-secondary-700">
                <li>Provide and improve our educational platform and mentorship services</li>
                <li>Match learners with appropriate mentors and resources</li>
                <li>Track learning progress and generate personalized recommendations</li>
                <li>Process donations and issue tax receipts</li>
                <li>Communicate program updates, opportunities, and organizational news</li>
                <li>Ensure platform security and prevent abuse</li>
                <li>Comply with legal obligations</li>
              </ul>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-secondary-900 mb-3">4. How We Share Your Information</h2>
              <p className="text-secondary-700 leading-relaxed">
                We do <strong>not</strong> sell your personal information. We may share data with:
              </p>
              <ul className="list-disc pl-5 space-y-1 text-secondary-700 mt-2">
                <li><strong>Service providers</strong> — Supabase (database hosting), Vercel (web hosting), and our donation processing platform, each bound by data processing agreements</li>
                <li><strong>AI services</strong> — Anonymized or pseudonymized content may be sent to Anthropic's Claude API for AI mentoring features. No personally identifiable information is shared beyond what you include in your prompts</li>
                <li><strong>Legal compliance</strong> — When required by law, subpoena, or to protect our rights</li>
                <li><strong>Aggregate reporting</strong> — De-identified, aggregate statistics may be shared in grant applications and impact reports</li>
              </ul>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-secondary-900 mb-3">5. Donor Privacy</h2>
              <p className="text-secondary-700 leading-relaxed">
                We respect donor privacy. Donation records (name, amount, date, contact information)
                are kept confidential. We do not sell, share, or trade donor information with any
                third party, except as required for payment processing and tax compliance. Donors may
                request to remain anonymous in any public acknowledgment.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-secondary-900 mb-3">6. Data Security</h2>
              <p className="text-secondary-700 leading-relaxed">
                We implement industry-standard security measures including encrypted connections
                (TLS/SSL), secure authentication via Supabase Auth, row-level security policies on
                our database, and rate limiting on API endpoints. However, no system is 100% secure,
                and we cannot guarantee absolute security of your data.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-secondary-900 mb-3">7. Data Retention</h2>
              <p className="text-secondary-700 leading-relaxed">
                We retain account data for as long as your account is active. If you request account
                deletion, we will remove your personal data within 30 days, except where retention
                is required by law (e.g., donation records for tax purposes are retained for 7 years).
              </p>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-secondary-900 mb-3">8. Your Rights</h2>
              <p className="text-secondary-700 leading-relaxed">You have the right to:</p>
              <ul className="list-disc pl-5 space-y-1 text-secondary-700 mt-2">
                <li>Access the personal data we hold about you</li>
                <li>Correct inaccurate information</li>
                <li>Request deletion of your account and data</li>
                <li>Opt out of non-essential communications</li>
                <li>Request a copy of your data in a portable format</li>
              </ul>
              <p className="text-secondary-700 leading-relaxed mt-2">
                To exercise any of these rights, email us at{' '}
                <a href={`mailto:${CONTACT_EMAIL}`} className="text-primary-600 hover:text-primary-500">
                  {CONTACT_EMAIL}
                </a>.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-secondary-900 mb-3">9. Children's Privacy</h2>
              <p className="text-secondary-700 leading-relaxed">
                Our services are not directed at individuals under 18 years of age. We do not
                knowingly collect personal information from minors.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-secondary-900 mb-3">10. Changes to This Policy</h2>
              <p className="text-secondary-700 leading-relaxed">
                We may update this Privacy Policy from time to time. We will notify registered users
                of material changes via email. The "Last updated" date at the top of this page
                reflects the most recent revision.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-secondary-900 mb-3">11. Contact Us</h2>
              <p className="text-secondary-700 leading-relaxed">
                If you have questions about this Privacy Policy or our data practices, contact us at:
              </p>
              <p className="text-secondary-700 mt-2">
                <strong>{ORG_NAME}</strong><br />
                Email:{' '}
                <a href={`mailto:${CONTACT_EMAIL}`} className="text-primary-600 hover:text-primary-500">
                  {CONTACT_EMAIL}
                </a>
              </p>
            </section>
          </div>
        </div>
      </main>

      <PublicFooter />
      <BackToTop />
    </div>
  );
};

export default PrivacyPolicy;
